import { useCallback, useEffect, useRef, useState } from "react";
import { CaseData, CaseStatus } from "@kahunmedical/common-wtypes";
import { FilteredVisitsVisits, FilterInput, Order } from "fe-common/graphql";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import useTimeout from "fe-common/hooks/useTimeout";
import { PatientShortSummary } from "../clinicTypes";
import { FREE_TEXT_FEATURE_ID } from "shared";
import { v4 as uuid } from "uuid";
import { useClinicMessagesContext } from "../../components/clinic/clinicMessagesContext";

export interface usePatientDataParams {
  clinicId: string;
  limit: number;
  sortBy: string;
  order: Order;
  queryFilter: FilterInput;
  finished: boolean;
  digitCode?: string;
}

export type PiiShortSummary = PatientShortSummary & {
  piiData: Record<string, string>;
  chiefComplaint?: {
    id: string;
    name: string;
    shortname: string;
    snomedid: string;
  };
};

export function usePatientData({
  clinicId,
  limit,
  sortBy,
  order,
  queryFilter,
  finished,
  digitCode,
}: usePatientDataParams) {
  const interval = 30000;
  const daoClinic = useClinicContext();
  const { setMessage } = useClinicMessagesContext();

  const [casesList, setCasesList] = useState<CaseData[]>([]);
  const [totalCases, setTotalCases] = useState<number>();
  const [loadingCasesList, setLoadingCasesList] = useState(false);
  const [loadingCaseData, setLoadingCaseData] = useState(false);
  const [loadedClinic, setLoadedClinic] = useState(null);
  const [errorMsg, setErrorMsg] = useState<string>(null);
  const [startKey, setStartKey] = useState();
  const [moreLoading, setMoreLoading] = useState(false);
  const [allowLoadingMore, setAllowLoadingMore] = useState(true);
  const patientCodesRef = useRef([]); //saves patient codes to check if the data for a case already exists
  const patientsLoadedRef = useRef(false);

  // const statusToShortSummery = useCallback(
  //   (statusData: CaseData, index, codes): PatientData => {
  //     let gender;
  //     const visitReason = statusData.visitReason?.pop?.();
  //     const piiData = {
  //       ...(statusData.piiPatientProperties || {}),
  //       display_name: statusData.piiPatientProperties
  //         ? statusData.piiPatientProperties.display_name ||
  //           [
  //             statusData.piiPatientProperties.first_name,
  //             statusData.piiPatientProperties.last_name,
  //           ]
  //             .filter(Boolean)
  //             .join(" ")
  //         : "",
  //       status: statusData.status,
  //     };
  //
  //     gender =
  //       statusData.gender === "male"
  //         ? "Male"
  //         : statusData.gender === "female"
  //         ? "Female"
  //         : "";
  //     //when its free text use it
  //     const main =
  //       codes[index].visitStatus?.chiefComplaint?.freeText ||
  //       statusData?.chiefComplaint?.shortname;
  //     return {
  //       code: codes[index],
  //       patientSummary: {
  //         age: statusData?.age,
  //         gender,
  //         main,
  //         visitReason,
  //         piiData,
  //       },
  //     };
  //   },
  //   [],
  // );

  const getCasesData = (visits: FilteredVisitsVisits[]): CaseData[] =>
    visits.map((visit) => {
      const visitData = visit.visitStatusStructure;
      const patientData = visit.patientProperties?.piiPatientProperties || [];
      return {
        caseId: visit.shareid,
        caseCode: visit.id,
        digitCode: visit.digitCode,
        status: visitData.status as CaseStatus,
        date: visit.date,
        visitDate: patientData.visit_date
          ? parseInt(patientData.visit_date)
          : null,
        visitReason: visitData.visitReason?.[0],
        patient: {
          age: visitData.age || null,
          gender: visitData.gender,
          displayName: patientData["display_name"],
          firstName: patientData["first_name"],
          lastName: patientData["last_name"],
        },
        chiefComplaint: visitData.chiefComplaint && {
          ...visitData.chiefComplaint,
          shortname:
            visitData.chiefComplaint.id !== FREE_TEXT_FEATURE_ID
              ? visitData.chiefComplaint.shortname
              : visit.visitStatus?.chiefComplaint.freeText, //TODO: Add freeText to "visitStatusStructure" in backend
        },
        comment: visit.comment,
        encounterText: patientData["encounter_text"],
      };
    });

  const currentSessionId = useRef<string>();
  const pollingTimeout = useTimeout(() => {
    getCasesList();
  }, interval);

  const getCasesList = useCallback(async () => {
    setMoreLoading(false);
    setLoadingCasesList(true);
    currentSessionId.current = uuid();
    patientsLoadedRef.current = true;
    let sessionId = currentSessionId.current;
    try {
      const visits = await daoClinic.recentVisitsFiltered(
        clinicId,
        queryFilter,
        null,
        sortBy,
        limit,
        finished,
        order,
      );
      if (sessionId === currentSessionId.current) {
        setStartKey(visits.startKey);
        const casesData = getCasesData(visits.visits as FilteredVisitsVisits[]);
        setCasesList(casesData);
        setTotalCases(visits.count);
        patientCodesRef.current = casesData.map((c) => c.digitCode);
        setLoadingCasesList(false);
      }
    } catch (e) {
      console.error(e);
      setErrorMsg(
        "There was problem loading the patients list! please try again",
      );
      setLoadingCasesList(false);
    } finally {
      patientsLoadedRef.current = true;
      setLoadedClinic(clinicId);
      pollingTimeout();
    }
  }, [
    daoClinic,
    clinicId,
    queryFilter,
    sortBy,
    limit,
    finished,
    order,
    pollingTimeout,
    setCasesList,
    setLoadingCasesList,
    setErrorMsg,
  ]);

  const getExpandedCase = useCallback(
    async (digitCode: string) => {
      setLoadingCaseData(true);
      try {
        const casesData = await daoClinic.getPatientFromCode(
          clinicId,
          digitCode,
        );
        if (!casesData) setErrorMsg("Case not found");
        else {
          setCasesList(getCasesData([casesData]));
        }
      } catch (e) {
        console.error(e);
        setErrorMsg("There was problem loading the case! please try again");
      } finally {
        setLoadingCaseData(false);
      }
    },
    [daoClinic, clinicId],
  );

  useEffect(() => {
    if (digitCode && !patientCodesRef.current.includes(digitCode)) {
      getExpandedCase(digitCode);
    } else if (!digitCode) {
      //setLoadedClinic(null);
      if (!patientCodesRef.current?.length) getCasesList();
    }
  }, [getExpandedCase, getCasesList, digitCode]);

  useEffect(() => {
    setLoadedClinic(null);
    getCasesList();
  }, [getCasesList, getExpandedCase, queryFilter, finished, sortBy, order]);

  //display error messages
  useEffect(() => {
    if (errorMsg) {
      setMessage({ text: errorMsg, severity: "error" });
    }
  }, [errorMsg, setMessage]);

  // const onSeenStatusChanged = useCallback(
  //   (code: string, userId: string, isSeen: boolean) => {
  //     daoClinic
  //       .updateSeenStatus(id, code, userId, isSeen)
  //       .then((updatedCode: any) => {
  //         setCasesList((oldData) => {
  //           const index = oldData.findIndex((od) => od.id === code);
  //           return [
  //             ...oldData.slice(0, index),
  //             {
  //               code: updatedCode,
  //               patientSummary: oldData[index].patientSummary,
  //             },
  //             ...oldData.slice(index + 1),
  //           ];
  //         });
  //       });
  //   },
  //   [daoClinic, id],
  // );

  const onLoadMore = useCallback(() => {
    setMoreLoading(true);
    daoClinic
      .recentVisitsFiltered(
        clinicId,
        queryFilter,
        startKey,
        sortBy,
        limit,
        finished,
        order,
      )
      .then((res) => {
        setStartKey(res.startKey);
        return res.visits as FilteredVisitsVisits[];
      })
      .then(getCasesData)
      .then((casesData) => {
        if (!casesData?.length) {
          setAllowLoadingMore(false);
        } else {
          setCasesList((existingData) => {
            const updatedData = [...existingData, ...casesData];
            patientCodesRef.current = casesData.map((c) => c.digitCode);
            return updatedData;
          });
        }
      })
      .catch((e) => {
        setErrorMsg("Could not load more patients");
        console.error(e);
      })
      .finally(() => setMoreLoading(false));
  }, [
    daoClinic,
    limit,
    queryFilter,
    finished,
    sortBy,
    order,
    clinicId,
    startKey,
  ]);

  const deleteCode = useCallback(
    async (codeIdToDelete: string): Promise<string> => {
      let status;
      const indexToDelete = casesList.findIndex(
        (pd) => pd.caseCode === codeIdToDelete,
      );
      if (indexToDelete > -1) {
        try {
          status = await daoClinic.deleteCode(
            clinicId,
            casesList[indexToDelete].caseCode,
          );

          if (status === "success") {
            setCasesList((oldPatientData) => [
              ...oldPatientData.slice(0, indexToDelete),
              ...oldPatientData.slice(indexToDelete + 1),
            ]);
            setMessage({
              text: "Patient delete successfully",
              severity: "success",
            });
          } else {
            console.error(
              "Error deleting code",
              casesList?.[indexToDelete]?.caseCode,
            );
          }
        } catch (err) {
          console.error(err);
        }
      }
      if (status !== "success") {
        setErrorMsg("Failed to delete patient");
      }
      return status;
    },
    [daoClinic, clinicId, casesList, setMessage],
  );

  const addComment = useCallback(
    async (digitCode: string, comment: string): Promise<string> => {
      let status;
      const currentIndex = casesList.findIndex(
        (pd) => pd.digitCode === digitCode,
      );
      if (currentIndex > -1) {
        status = await daoClinic.addUserComment(
          clinicId,
          casesList[currentIndex].caseCode,
          comment,
        );
        if (status === "success") {
          // update current state
          setCasesList((oldPatientData) => [
            ...oldPatientData.slice(0, currentIndex),
            {
              ...oldPatientData[currentIndex],
              comment,
            },
            ...oldPatientData.slice(currentIndex + 1),
          ]);
          setMessage({ text: "Comment saved", severity: "success" });
        } else {
          console.error("Error updating comment", digitCode, comment);
        }
      }
      if (status !== "success") {
        setErrorMsg("Failed to update comment");
      }
      return status;
    },
    [daoClinic, clinicId, casesList, setMessage],
  );

  return {
    casesList,
    loadingCasesList,
    patientData: casesList,
    loadingPatientsList: loadingCasesList,
    loadingCaseData,
    loadedClinic,
    setLoadingCaseData,
    onLoadMore,
    hasMore: !!startKey,
    moreLoading,
    allowLoadingMore,
    deleteCode,
    addComment,
    totalCases,
    getCasesList,
  };
}
